import { createSelector } from 'reselect'
import { saveDescriptionOfMerchandiseLineItem } from '../api/setFormData'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import { getJobWheels } from '../../util/index'
import { getQueryVariable } from '../../apiCalls/jobInfo'
import {
  isMeltSmeltCastExemptCOO,
  getSteelMeltAndPourCodes,
  getAluminumMeltAndPourCodes,
  getCopperSmeltAndCastCodes,
  russiaMetaData,
} from '../formSections/util/constants'

const MELT_SMELT_CAST_TYPE_CODES = [
  'STEEL_MELT_AND_POUR_COUNTRY_DETAIL',
  'ALUMINUM_SMELT_AND_CAST_COUNTRY_DETAIL',
  'COPPER_SMELT_AND_CAST_COUNTRY_DETAIL',
]

/** Hide + clear Aluminum / Steel / Copper declarations when COO becomes exempt. */
const hideMeltSmeltCastDeclarations = (declarationList = []) =>
  (declarationList || [])
    .filter((a) => !MELT_SMELT_CAST_TYPE_CODES.includes(a.typeCode))
    .concat(
      {
        typeCode: 'STEEL_MELT_AND_POUR_COUNTRY_DETAIL',
        steelMeltAndPourCountryDetail: {
          countryOfMeltAndPour: 'no',
          countryOfMeltAndPourCode: '',
          countryOfMeltAndPourApplicabilityCode: 'no',
        },
      },
      {
        typeCode: 'ALUMINUM_SMELT_AND_CAST_COUNTRY_DETAIL',
        aluminumSmeltAndCastCountryDetail: {
          doNotShowAluminum: true,
          isUsProduct: ' ',
          isPrimaryCountryOfSmeltApplied: 'no',
          primaryCountryOfSmelt: ' ',
          isSecondCountryOfSmeltApplied: 'no',
          secondaryCountryOfSmelt: ' ',
          countryOfCast: ' ',
        },
      },
      {
        typeCode: 'COPPER_SMELT_AND_CAST_COUNTRY_DETAIL',
        copperSmeltAndCastCountryDetail: {
          doNotShowCopper: true,
          isSecondCountryOfSmeltApplied: 'no',
          primaryCountryOfSmelt: ' ',
          secondaryCountryOfSmelt: ' ',
          countryOfMostRecentCast: ' ',
        },
      }
    )

const EMPTY_COUNTRY = new Set(['', 'N/A', '_BLANK_', 'NONE']);

const isCountryEmpty = (value) => EMPTY_COUNTRY.has(String(value ?? '').trim());

const setCountryIfEmpty = (currentValue, originCountry) =>
  isCountryEmpty(currentValue) ? originCountry : currentValue;

const setSecondaryToNA = (detail) =>
  isCountryEmpty(detail.secondaryCountryOfSmelt)
    ? { ...detail, isSecondCountryOfSmeltApplied: 'no', secondaryCountryOfSmelt: '' }
    : detail;

/** Fill metal country fields from Country of Origin only when empty. Secondary smelt stays N/A. */
export const fillEmptyMetalCountries = (declarationList = [], countryOfOrigin) => {
  const originCountry = String(countryOfOrigin || '').trim().toUpperCase();
  if (!originCountry || isMeltSmeltCastExemptCOO(originCountry)) return declarationList;

  return (declarationList || []).map((item) => {
    if (item?.typeCode === 'ALUMINUM_SMELT_AND_CAST_COUNTRY_DETAIL') {
      const aluminum = item.aluminumSmeltAndCastCountryDetail;
      if (!aluminum || aluminum.doNotShowAluminum) return item;
      const updatedAluminum = setSecondaryToNA({
        ...aluminum,
        primaryCountryOfSmelt:
          aluminum.isPrimaryCountryOfSmeltApplied === 'no'
            ? aluminum.primaryCountryOfSmelt
            : setCountryIfEmpty(aluminum.primaryCountryOfSmelt, originCountry),
        countryOfCast: setCountryIfEmpty(aluminum.countryOfCast, originCountry),
      });
      return isEqual(updatedAluminum, aluminum)
        ? item
        : { ...item, aluminumSmeltAndCastCountryDetail: updatedAluminum };
    }

    if (item?.typeCode === 'STEEL_MELT_AND_POUR_COUNTRY_DETAIL') {
      const steel = item.steelMeltAndPourCountryDetail;
      if (!steel || steel.countryOfMeltAndPour === 'no' || steel.countryOfMeltAndPourApplicabilityCode === 'yes') {
        return item;
      }
      const countryOfMeltAndPourCode = setCountryIfEmpty(steel.countryOfMeltAndPourCode, originCountry);
      return countryOfMeltAndPourCode === steel.countryOfMeltAndPourCode
        ? item
        : { ...item, steelMeltAndPourCountryDetail: { ...steel, countryOfMeltAndPourCode } };
    }

    if (item?.typeCode === 'COPPER_SMELT_AND_CAST_COUNTRY_DETAIL') {
      const copper = item.copperSmeltAndCastCountryDetail;
      if (!copper || copper.doNotShowCopper) return item;
      const updatedCopper = setSecondaryToNA({
        ...copper,
        primaryCountryOfSmelt: setCountryIfEmpty(copper.primaryCountryOfSmelt, originCountry),
        countryOfMostRecentCast: setCountryIfEmpty(copper.countryOfMostRecentCast, originCountry),
      });
      return isEqual(updatedCopper, copper)
        ? item
        : { ...item, copperSmeltAndCastCountryDetail: updatedCopper };
    }

    return item;
  });
};

/** Re-show Aluminum / Steel / Copper when COO leaves exempt list and HTS still matches. */
const showMeltSmeltCastDeclarationsForHts = (hts, declarationList = [], dateContext = {}) => {
  if (!hts) return declarationList;
  const cleanHts = String(hts).replaceAll('.', '');
  let updatedList = [...(declarationList || [])];

  const isSteelHts = !!getSteelMeltAndPourCodes(dateContext).find((xxx) => cleanHts.startsWith(xxx));
  if (isSteelHts) {
    const existingSteel = updatedList.find(
      (aaa) =>
        aaa.typeCode === 'STEEL_MELT_AND_POUR_COUNTRY_DETAIL' &&
        aaa.steelMeltAndPourCountryDetail?.countryOfMeltAndPour !== 'no'
    );
    if (!existingSteel) {
      updatedList = updatedList
        .filter((aaa) => aaa.typeCode !== 'STEEL_MELT_AND_POUR_COUNTRY_DETAIL')
        .concat({
          typeCode: 'STEEL_MELT_AND_POUR_COUNTRY_DETAIL',
          steelMeltAndPourCountryDetail: {
            countryOfMeltAndPour: 'yes',
            countryOfMeltAndPourCode: '',
            countryOfMeltAndPourApplicabilityCode: 'no',
          },
        });
    }
  }

  const russiaHts = Object.values(russiaMetaData)
    .map((a) => Object.keys(a))
    .reduce((a, b) => a.concat(b), []);
  let addAluminum =
    russiaHts.some((t) => cleanHts.startsWith(t)) ||
    getAluminumMeltAndPourCodes(dateContext).find((htsCode) => cleanHts.startsWith(htsCode)) !== undefined;
  if (addAluminum) {
    const existingAluminum = updatedList.find((a) => a.typeCode === 'ALUMINUM_SMELT_AND_CAST_COUNTRY_DETAIL');
    if (!existingAluminum || existingAluminum?.aluminumSmeltAndCastCountryDetail?.doNotShowAluminum) {
      updatedList = updatedList
        .filter((a) => a.typeCode !== 'ALUMINUM_SMELT_AND_CAST_COUNTRY_DETAIL')
        .concat({
          typeCode: 'ALUMINUM_SMELT_AND_CAST_COUNTRY_DETAIL',
          aluminumSmeltAndCastCountryDetail: {
            doNotShowAluminum: false,
            isUsProduct: 'no',
            isPrimaryCountryOfSmeltApplied: 'yes',
            primaryCountryOfSmelt: '',
            isSecondCountryOfSmeltApplied: 'yes',
            secondaryCountryOfSmelt: '',
            countryOfCast: '',
          },
        });
    }
  }

  const addCopper =
    getCopperSmeltAndCastCodes().find((htsCode) => cleanHts.startsWith(htsCode)) !== undefined;
  if (addCopper) {
    const existingCopper = updatedList.find((a) => a.typeCode === 'COPPER_SMELT_AND_CAST_COUNTRY_DETAIL');
    if (!existingCopper || existingCopper?.copperSmeltAndCastCountryDetail?.doNotShowCopper) {
      updatedList = updatedList
        .filter((a) => a.typeCode !== 'COPPER_SMELT_AND_CAST_COUNTRY_DETAIL')
        .concat({
          typeCode: 'COPPER_SMELT_AND_CAST_COUNTRY_DETAIL',
          copperSmeltAndCastCountryDetail: {
            doNotShowCopper: false,
            isSecondCountryOfSmeltApplied: 'yes',
            primaryCountryOfSmelt: '',
            secondaryCountryOfSmelt: '',
            countryOfMostRecentCast: '',
          },
        });
    }
  }

  return updatedList;
}

export const getCookie = (cname) => {
  var name = cname + "="
  var decodedCookie = decodeURIComponent(document.cookie)
  var ca = decodedCookie.split(';')
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i]
    while (c.charAt(0) === ' ') {
      c = c.substring(1)
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length)
    }
  }
  return ''
}

export const renumberLaceyActContainers = (containers) => (
  containers.map((container, index) => {
    let containerKeyInfo = container.key.split('.')
    containerKeyInfo[1] = index + 1
    container.key = containerKeyInfo.join('.')
    return container
  })
)

export const renumberPGAItems = (items) => (
  items.map((item, index) => {
    for (let type in item) {
      let itemKeyInfo = item[type].key.split('.')
      itemKeyInfo[1] = index + 1
      item[type].key = itemKeyInfo.join('.')
    }
    return item
  })
)


// Formats flat HTS with appropiate '.'
export const formatHTS = hts => {
  if (hts !== undefined) {
    let newHts = hts.replace(/[.]/g, '')
    if (newHts.length < 5) return hts
    if (newHts.length >= 5 && newHts.length <= 6) {
      newHts = insertIntoString(newHts, 4, '.')
      return newHts
    }
    if (newHts.length >= 7 && newHts.length <= 8) {
      newHts = insertIntoString(newHts, 4, '.')
      newHts = insertIntoString(newHts, 7, '.')
      return newHts
    }
    if (newHts.length >= 9) {
      newHts = insertIntoString(newHts, 4, '.')
      newHts = insertIntoString(newHts, 7, '.')
      newHts = insertIntoString(newHts, 10, '.')
      return newHts
    }
  }
}


export const hasFieldKN = (stringInput) => {
  if(stringInput == -999)return false; 
  if(stringInput == "_BLANK_")return false;
  return !!((stringInput||"")+"").trim();
}

export const allPossibleChinaExlcusionHTSTriggers =()=> {
  return ["99038867","99038866","99038869"];
}
export const chinaExlcusionHTSTriggers = (entryDate)=>{
  const JUNE_15_2024 = 1718409600000;
  if(!entryDate)return["99038867"];
  let isBetweenJune2023andDecember2021 = (entryDate<1685602800000 && entryDate>1641024000000);
  if(isBetweenJune2023andDecember2021)return["99038867","99038866"];
  if(entryDate >= JUNE_15_2024){
    return ["99038869"];
  }
  return["99038867"];
}

export const formatHTSTree = hts => {
  if (hts !== undefined) {
    let newHts = hts.replace(/[.]/g, '')
    if (newHts.length <= 3) return newHts.slice(0, 2)
    if (newHts.length <= 5 && newHts.length <= 6) {
      return newHts.slice(0, 4)
    }
    else {
      let cutHTS = newHts.slice(0, 6)
      cutHTS = insertIntoString(cutHTS, 4, '.')
      return cutHTS
    }
  }
}

export const insertIntoString = (str, index, value) => {
  return str.substr(0, index) + value + str.substr(index)
}

export const  changeCountryOfOrigin = (newCountryOfOrigin, props, getPreloadedMetadata) =>{
  let {countryOfOrigin,lineItemIndex,invoiceItemIndex,
    updateMerchLineItemFeild,
    saveDescriptionOfMerchandiseLine,
    setHSDTL,
    handleLineItemContextSwitch,
    htsBasedAvailableCodes,
    updateTradeAgreementListOnCountryChange,
    merchandiseItem,
    itDate,
    entryDate,
    invoiceId,
    merchandiseId,
    updateMerchLineItemObject
  } = props;
  updateMerchLineItemFeild(
    lineItemIndex,invoiceItemIndex,
    'countryOfOrigin',
    newCountryOfOrigin
  )
  const myDuty = props?.merchandiseItem?.merchandiseDuties?.find(aaa => aaa.isCommodityDuty == 1)
    || props?.merchandiseDuties?.find(aaa => aaa.isCommodityDuty == 1);
  let merchandiseDutyEv = [
    {
      "enteredValue": myDuty.enteredValue,
      "merchandiseDutyId": myDuty.merchandiseDutyId,
      "isCommodityDuty": myDuty.isCommodityDuty
    }]

  // Hide/clear when COO becomes exempt; re-show when leaving exempt and HTS matches
  const commodityHts = myDuty?.htsus;
  if (commodityHts && updateMerchLineItemObject && saveDescriptionOfMerchandiseLine) {
    const lineItem = merchandiseItem || props.thisMerchandiseItem;
    const currentList =
      get(lineItem, 'importerAdditionalDeclarationDetails.importerAdditionalDeclarationDetail', null) ||
      get(props, 'importerAdditionalDeclarationDetails.importerAdditionalDeclarationDetail', []) ||
      [];
    const dateContext = {
      itDate,
      entryDate,
      releaseDate: props.releaseDate,
    };
    const updatedList = isMeltSmeltCastExemptCOO(newCountryOfOrigin)
      ? hideMeltSmeltCastDeclarations(currentList)
      : fillEmptyMetalCountries(
          showMeltSmeltCastDeclarationsForHts(commodityHts, currentList, dateContext),
          newCountryOfOrigin
        );
    if (!isEqual(updatedList, currentList)) {
      updateMerchLineItemObject(
        lineItemIndex,
        invoiceItemIndex,
        'importerAdditionalDeclarationDetails',
        'importerAdditionalDeclarationDetail',
        updatedList
      );
      saveDescriptionOfMerchandiseLine(
        lineItemIndex,
        invoiceItemIndex,
        'importerAdditionalDeclarationDetails',
        { importerAdditionalDeclarationDetail: updatedList }
      );
    }
  }

  saveDescriptionOfMerchandiseLineItem(merchandiseId, invoiceId , 'countryOfOrigin', newCountryOfOrigin, myDuty?{addedParametersx:{merchandiseDuties:merchandiseDutyEv}}:{}).then((resp)=>{
    handleLineItemContextSwitch(lineItemIndex , invoiceItemIndex)

    if(countryOfOrigin!==newCountryOfOrigin){

      if(merchandiseItem && merchandiseItem.merchandiseDuties && merchandiseItem.merchandiseDuties.length){
        let hts = merchandiseItem.merchandiseDuties[0].htsus;
        // Use setTimeout to ensure Redux state has been updated with additionalHtsInfo (EUROPE_SET)
        // This ensures EUROPE_SET is available when setHSDTL runs after GET_HTS_ADDITIONAL_INFO completes
        setTimeout(() => {
          const preloadedMetadata = typeof getPreloadedMetadata === 'function' ? getPreloadedMetadata() : getPreloadedMetadata;
          setHSDTL(
            itDate,
            props.EntryTypeInfo,
            hts,invoiceItemIndex,
            lineItemIndex,
            0,
            1,
            false,
            props.invoiceId,
            props.merchandiseId,
            newCountryOfOrigin,
            props.merchandiseItem,
            undefined,
            undefined,
            preloadedMetadata
          )
        }, 0);
      }
    }
    
    if(htsBasedAvailableCodes){
      updateTradeAgreementListOnCountryChange(lineItemIndex,invoiceItemIndex, newCountryOfOrigin)
    }
  })
  
}

export const resetDutyItemFieldsInNonCommodity= (updateMerchDutyItemObject,lineItemIndex,invoiceItemIndex,myindexofduty,saveMerchDutyItem,updateMerchDutyItemFeild) =>  {
  updateMerchDutyItemObject(
    lineItemIndex,invoiceItemIndex,
    myindexofduty,
    'manifestQuantity',
    'units',
    "_BLANK_"
  )
  updateMerchDutyItemObject(
    lineItemIndex,invoiceItemIndex,
    myindexofduty,
    'additionalQuantity',
    'units',
    "_BLANK_"
  )
  updateMerchDutyItemObject(
    lineItemIndex,invoiceItemIndex,
    myindexofduty,
    'manifestQuantity',
    'quantity',
    parseFloat(0.00).toFixed(2)
  )
  saveMerchDutyItem(lineItemIndex,invoiceItemIndex, myindexofduty, 'manifestQuantity')
  updateMerchDutyItemObject(
    lineItemIndex,invoiceItemIndex,
    myindexofduty,
    'additionalQuantity',
    'quantity',
    parseFloat(0.00).toFixed(2)
  )
  saveMerchDutyItem(lineItemIndex,invoiceItemIndex, myindexofduty, 'additionalQuantity')
  updateMerchDutyItemFeild(invoiceItemIndex,
    lineItemIndex,
    myindexofduty,
    'enteredValue',
    parseFloat(0.00).toFixed(2)
  )
  saveMerchDutyItem(lineItemIndex,invoiceItemIndex, myindexofduty, 'enteredValue')
  updateMerchDutyItemFeild(invoiceItemIndex,
    lineItemIndex,
    myindexofduty,
    'invoiceCurrencyValue',
    parseFloat(0.00).toFixed(2)
  )
  saveMerchDutyItem(lineItemIndex,invoiceItemIndex, myindexofduty, 'invoiceCurrencyValue')

}

export const fetchSelectedReasonCodes = (reasons=[], key) => {
  let codes = [];
  reasons.map((item)=>{
    if(item.key != null && key != null && String(item.key) === String(key)){
      item.reasonCodes && item.reasonCodes.map((_item)=>{
        codes.push(_item.code);
      })
    }
  })
  return codes;
}

export const totalHeaderReasonSelected = (pscData) => {
  let totalHeaderReasonSelected =  pscData.headerCodes ? pscData.headerCodes.map((item)=>{
    return item.reasonCodes ? item.reasonCodes.length : 0
  }).reduce((a, b) => a + b, 0) : 0;
  return totalHeaderReasonSelected;
}

export const shouldMakeHTSAPICall = (htsValue, isCommodity) => {
  if (!htsValue) return false;
  
  const htsLength = htsValue.replace(/\./g, "").length;
  const isMainHTS = isCommodity === 1;
  const isAdditionalHTS = isCommodity === 2;
  
  return htsLength === 8 || htsLength === 10;
}

export const getContainerList = createSelector(
  [state => state.BOLInfo?.container || []],
  (containers) => containers.map(containerItem =>
    containerItem.containerId.concat(';', containerItem.number)
  )
)

// Memoized selector for field metadata
export const getFieldMetadata = createSelector(
  [state => state.FormValues, (_, props) => props?.formDisabled],
  (formValues, formDisabled) => ({
    newValue: formValues?.newValue,
    rbCounterID: formValues?.newValue,
    fieldName: formValues?.fieldName,
    setINPUTID: formValues?.setINPUTID,
    penClicked: formValues?.penClicked,
    shipmentId: formValues?.shipmentId,
    disabled: Boolean(formDisabled),
  })
);

export {
  pgaLabels,
  pgaErrors
} from '../text/pga'

// Gets HTS chapter number from HTS code
export const getHtsChapter = (htsus) => {
  if (!htsus) return null;
  const chapter = parseInt(htsus.slice(0, 2));
  return chapter;
};

// Checks if HTS is Chapter 99 duty (excludes 99039008 Russia tariff)
export const isChapter99Duty = (htsus) => {
  if (!htsus) return false;
  const htsChapter = getHtsChapter(htsus);
  if (htsChapter !== 99) return false;
  const htsWithoutDots = htsus.replace(/\./g, "");
  return htsWithoutDots !== "99039008";
};

// Checks if Chapter 91 duties exist in the duties list
export const hasChapter91Duties = (allDuties) => {
  if (!allDuties || !Array.isArray(allDuties)) return false;
  const hasCh91 = allDuties.some((dutyItem) => {
    const chapter = getHtsChapter(dutyItem?.htsus);
    return chapter === 91;
  });
  return hasCh91;
};

// Calculates total entered value from all Chapter 91 HTS codes for Chapter 99, uses adjustedValue when deductions exist (0 if -999), otherwise uses enteredValue
export const getTotalChapter91EnteredValue = (allDuties) => {
  if (!allDuties || !Array.isArray(allDuties)) return 0;
  
  const chapter91Duties = allDuties.filter((dutyItem) => {
    const chapter = getHtsChapter(dutyItem?.htsus);
    return chapter === 91;
  });
  
  const totalValue = chapter91Duties.reduce((sum, dutyItem) => {
    const hasAdditionsOrDeductions = 
      (parseFloat(dutyItem?.deletions?.totalDeletions || 0) > 0) || 
      (parseFloat(dutyItem?.additions?.totalAdditions || 0) > 0);
    
    let value;
    if (hasAdditionsOrDeductions) {
      const adjustedVal = parseFloat(dutyItem.adjustedValue);
      // Use 0 if adjustedValue is -999 (deduction equals entered value), otherwise use adjustedValue
      value = adjustedVal === -999 ? 0 : (adjustedVal || 0);
    } else {
      value = parseFloat(dutyItem.enteredValue) || 0;
    }
    
    return sum + value;
  }, 0);
  
  return totalValue;
};

// Use aggregated Chapter 91 values for Chapter 99 when enteredValue field is disabled
export const shouldUseChapter91ForCh99 = (duty) => {
  if (!duty) return false;
  const isCh99 = isChapter99Duty(duty.htsus);
  const isNonCommodity = duty.isCommodityDuty !== 1;
  return isCh99 && isNonCommodity;
};

// Checks if enteredValue field is disabled for a duty item
export const isEnteredValueDisabled = (duty) => {
  if (!duty) return false;
  
  const htsus = duty.htsus || "";
  const htsChapter = getHtsChapter(htsus);
  const is9802Hts = htsus.startsWith("9802") && duty.isCommodityDuty !== 1;
  const isCommodityHTS = (htsChapter !== 98 && htsChapter !== 99) || is9802Hts;
  const isCommodityDuty = duty.isCommodityDuty === 1;
  
  return !(isCommodityHTS || isCommodityDuty);
};

// Gets duty computation code from hsDataDtl
export const getDutyComputationCode = (hsDataDtl) => {
  const parsedCode = parseInt(hsDataDtl?.dutyComputationCode);
  return isNaN(parsedCode) ? null : parsedCode;
};

/**
 * Checks if the current flow is STC/DHL flow
 */
export const isStcFlow = (allpropsaa = {}, currentJobData = null) => {
  const jobData = currentJobData || allpropsaa?.currentJobData || window.currentJobData;
  if (!jobData) return false;
  
  const jobWheel = getJobWheels((window.shipmentCountry || 'US').toUpperCase(), jobData);
  return !!get(jobWheel, "STC_JOB_WHEEL.value.state");
};

export const getFromTab = () => getQueryVariable('fromTab');

export const isEngineFromTabEntry = () => getFromTab() === 'entry';

export const shouldAutoRedirectToPscChild = (shipId) => {
  if (isEngineFromTabEntry()) return false;
  if ((shipId || '').includes('--')) return false;
  if (window.GLOBAL_HAS_REDIRECTED_USING_PSCCHAIN) return false;
  if (window.GLOBAL_USER_SELECTED_SHIPMENT_ID === shipId) return false;
  return true;
};

/** fromTab only controls which form is opened; API id still prefers PSC chain child. */
export const getEnginePscEffectiveShipmentId = (fallbackId, pscChain, pscShipmentId) => {
  const pscChildId =
    resolvePscTargetShipmentId(pscChain) ||
    pscShipmentId ||
    window.pscShipmentId;
  return pscChildId && !(fallbackId || '').includes('--') ? pscChildId : fallbackId;
};

// PSC chain tail shipment id for form load: activeChild match, current row, last non-archived, then activeChildId.
export const resolvePscTargetShipmentId = (pscChain) => {
  if (!pscChain) return undefined;
  const rows = pscChain.child || [];
  const activeChildShipmentId = pscChain.activeChildId;
  const lastCreatedShipmentId = pscChain.lastCreatedId;
  const allowedTailIdShipmentId = pscChain.allowedTailId;

  const targetShipmentId =
  activeChildShipmentId ||
    rows.find(
      (row) => row.humanReadableId === lastCreatedShipmentId && !row.archived
    )?.humanReadableId ||
    rows.find((row) => row.current && !row.archived)?.humanReadableId
    || allowedTailIdShipmentId
    
  return targetShipmentId;
};

// Checks if HTS chapter is a commodity chapter (not 98 or 99)
export const isCommodityHtsChapter = (htsChapter) => {
  return ![98, 99].includes(htsChapter);
};

export {
  isEngineView,
  clearEnginePscNavigation,
  reloadEnginePscChildForm,
  navigateEngineToPscChild,
  refreshEnginePscChildAfterUpdate,
} from './enginePscNavigation';